/**
 * Corridor — drawing primitives for a flight down a tubular space.
 *
 * Every function here takes a camera and a 2D context and nothing else. They
 * hold no state and know nothing about React, so a scene composes them in
 * whatever order it wants, and any other surface can reuse them.
 */

import { PinholeCamera, Rgb, rgba, mulberry32, clamp } from './camera';

export interface Mote {
    x: number;
    y: number;
    z: number;
    /** World-space radius. Small — these are motes, not planets. */
    r: number;
    /** 0..1 selector deciding whether this mote takes the accent colour */
    tint: number;
}

export interface DustFieldOptions {
    count: number;
    /** Motes live in an annulus so the flight path down the middle stays clear */
    innerRadius: number;
    outerRadius: number;
    /** Total Z extent the field wraps around */
    depth: number;
    seed?: number;
}

/**
 * Generate a dust field once. Seeded, so it is byte-identical across reloads
 * and resizes.
 */
export function createDustField(o: DustFieldOptions): Mote[] {
    const rnd = mulberry32(o.seed ?? 1);
    const motes: Mote[] = [];
    for (let i = 0; i < o.count; i++) {
        const ang = rnd() * Math.PI * 2;
        const rad = o.innerRadius + rnd() * (o.outerRadius - o.innerRadius);
        motes.push({
            x: Math.cos(ang) * rad,
            y: Math.sin(ang) * rad * 0.72,
            z: rnd() * o.depth,
            r: 0.5 + rnd() * 1.6,
            tint: rnd(),
        });
    }
    return motes;
}

/** Max on-screen radius of a mote. Past this it stops reading as dust. */
const MOTE_MAX_PX = 2.4;
/** Motes fade out as they rush the lens, instead of ballooning across it. */
const MOTE_NEAR_FADE = 420;

export function drawDust(
    ctx: CanvasRenderingContext2D,
    cam: PinholeCamera,
    motes: Mote[],
    accent: Rgb,
    neutral: Rgb,
    wrapDepth: number,
    camZ: number,
    speed: number,
): void {
    const vanishing = cam.vanishing;
    // Streaks only appear once the camera is genuinely moving; a static frame
    // should show points, not smears.
    const streak = clamp(speed * 1.5, 0, 150);

    for (let i = 0; i < motes.length; i++) {
        const m = motes[i];
        // wrap the mote ahead of the camera so the corridor never runs out
        let z = m.z;
        if (z - camZ < cam.near) {
            z += Math.ceil((camZ + cam.far - z) / wrapDepth) * wrapDepth;
        }
        const p = cam.project(m.x, m.y, z);
        if (!p) continue;

        const fog = cam.fog(p.depth);
        if (fog <= 0.002) continue;

        const nearFade = clamp((p.depth - cam.near) / MOTE_NEAR_FADE, 0, 1);
        const alpha = fog * nearFade * 0.9;
        if (alpha <= 0.004) continue;

        const size = clamp(m.r * p.k, 0.35, MOTE_MAX_PX);
        const colour = m.tint > 0.74 ? rgba(accent, alpha) : rgba(neutral, alpha * 0.5);

        if (streak > 3) {
            // A mote streaks along its radial direction of travel — the same
            // direction it is actually moving on screen as the camera advances.
            const dx = p.x - vanishing.x;
            const dy = p.y - vanishing.y;
            const len = Math.hypot(dx, dy) || 1;
            const s = streak * p.k * 1.8;
            ctx.strokeStyle = colour;
            ctx.lineWidth = size * 1.6;
            ctx.lineCap = 'round';
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p.x - (dx / len) * s, p.y - (dy / len) * s);
            ctx.stroke();
        } else {
            ctx.fillStyle = colour;
            ctx.beginPath();
            ctx.arc(p.x, p.y, size, 0, Math.PI * 2);
            ctx.fill();
        }
    }
}

/**
 * Evenly spaced rings receding to the vanishing point. This is what turns an
 * empty void into a place with a shape and a speed.
 */
export function drawRings(
    ctx: CanvasRenderingContext2D,
    cam: PinholeCamera,
    accent: Rgb,
    camZ: number,
    spacing: number,
    radius: number,
    roll: number,
    /** Multiplier on ring opacity — paper needs far more than a dark void. */
    gain = 1,
): void {
    const first = Math.floor(camZ / spacing) * spacing;
    for (let z = first; z < camZ + cam.far; z += spacing) {
        const p = cam.project(0, 0, z);
        if (!p) continue;
        const fog = cam.fog(p.depth);
        if (fog <= 0.004) continue;

        const rx = radius * p.k;
        if (rx < 6) continue;
        // rings brighten as they approach, so the eye can read the speed
        const approach = clamp(700 / p.depth, 0, 1);
        ctx.beginPath();
        ctx.ellipse(p.x, p.y, rx, rx * 0.72, roll, 0, Math.PI * 2);
        ctx.strokeStyle = rgba(accent, Math.min(1, fog * 0.34 * approach * gain));
        ctx.lineWidth = Math.max(0.5, 1.5 * approach);
        ctx.stroke();
    }
}

/**
 * A gate marks a chamber — the aperture the camera flies through as one stage
 * of the journey hands off to the next.
 */
export function drawGate(
    ctx: CanvasRenderingContext2D,
    cam: PinholeCamera,
    colour: Rgb,
    z: number,
    radius: number,
    roll: number,
): void {
    const p = cam.project(0, 0, z);
    if (!p) return;
    const fog = cam.fog(p.depth);
    if (fog <= 0.004) return;

    const rx = radius * p.k;
    const ry = rx * 0.72;

    const glow = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, Math.max(1, rx));
    glow.addColorStop(0, rgba(colour, fog * 0.14));
    glow.addColorStop(1, rgba(colour, 0));
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y, rx, ry, roll, 0, Math.PI * 2);
    ctx.fill();

    ctx.beginPath();
    ctx.ellipse(p.x, p.y, rx, ry, roll, 0, Math.PI * 2);
    ctx.strokeStyle = rgba(colour, Math.min(0.9, fog * 1.1));
    ctx.lineWidth = 2;
    ctx.stroke();
}
